import React, { useCallback, useMemo, useState } from 'react';
import { Badge, IconButton, SxProps, TableCell } from '@mui/material';
import { bodyCellStyles } from './styles';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { WorklogsField, WorklogsSection } from '../../types';
import { KpiFloatingComment } from './KpiFloatingComment';
import {
  AllCommentsGenericDocument,
  useCreateCommentMutation,
  useUpdateCommentMutation,
} from '@/__generated__/graphql';
import { useAtomValue } from 'jotai';
import * as kpiState from '../../state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { COLLECTION_NAMES } from '@/constants';
import { formatDateToYYYYMMDD, generateKpiKey, isSameCalendarDay } from '../../utils';
import { KpiFloatingCommentViewer } from './KpiFloatingCommentViewer';
import { KPI_NOTE_LOG_METADATA_KEY, KPI_NOTE_LOG_SHIFT_METADATA_KEY } from '../../constants';
import { isDate } from 'date-fns';

type Props = {
  field: WorklogsField;
  section: WorklogsSection;
};

const cellStyles: SxProps = {
  ...bodyCellStyles,
  textAlign: 'center',
  border: 1,
  borderColor: 'divider',
  borderLeft: 0,
  width: '10%',
};

export const KpiCommentTableCell = ({ field, section }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const selectedWorkpackIdForKpiTracker = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const workpackComments = useAtomValue(kpiState.workpackComments);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const key = generateKpiKey({ fieldKey: field.key || '', sectionKey: section.key });
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});

  const [createComment, { loading: createLoading }] = useCreateCommentMutation({
    awaitRefetchQueries: true,
    refetchQueries: [AllCommentsGenericDocument],
  });

  const [updateComment, { loading: updateLoading }] = useUpdateCommentMutation({
    awaitRefetchQueries: true,
    refetchQueries: [AllCommentsGenericDocument],
  });

  const comment = useMemo(() => {
    if (!workpackComments?.length) return;

    // Filter by todays date and shift
    const todayComments = workpackComments.filter((comment) => {
      const date = new Date(comment?.metadata?.[KPI_NOTE_LOG_METADATA_KEY]);
      if (!isDate(date)) return false;
      const isSameDate = isSameCalendarDay(date, selectedDate);
      const commentShift = comment?.metadata?.[KPI_NOTE_LOG_SHIFT_METADATA_KEY];
      const isSameShift = commentShift === selectedShift;
      return isSameDate && isSameShift;
    });

    return todayComments.find((comment) => comment.key === key);
  }, [workpackComments, selectedDate, selectedShift, key]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setValue('');
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (!comment) {
      showSnackBar('Comment not found', 'error');
      return;
    }
    setValue(comment.text);
    setIsEditing(true);
  };

  const handleSubmit = useCallback(async () => {
    // Validate that comment text is not empty
    if (!value.trim()) {
      showSnackBar('Comment text cannot be empty', 'error');
      return;
    }

    try {
      if (comment) {
        // Update existing comment
        await updateComment({
          variables: {
            input: {
              id: comment.id,
              text: value,
              structureId,
            },
          },
        });
      } else {
        // Create new comment
        if (!selectedWorkpackIdForKpiTracker) throw new Error('Workpack ID is not available');
        await createComment({
          variables: {
            input: {
              collectionName: COLLECTION_NAMES.WORKPACK,
              referenceId: selectedWorkpackIdForKpiTracker,
              structureId,
              text: value,
              key,
              metadata: {
                [KPI_NOTE_LOG_METADATA_KEY]: formatDateToYYYYMMDD(selectedDate),
                [KPI_NOTE_LOG_SHIFT_METADATA_KEY]: selectedShift,
              },
            },
          },
        });
      }

      setValue('');
      handleClose();
    } catch (error: unknown) {
      const message = extractErrorMessageFromUnknownError(error);
      showSnackBar(message, 'error');
    }
  }, [
    comment,
    createComment,
    key,
    selectedWorkpackIdForKpiTracker,
    showSnackBar,
    structureId,
    updateComment,
    value,
    selectedDate,
    selectedShift,
  ]);

  return (
    <TableCell sx={cellStyles}>
      <IconButton onClick={handleOpen} size="small" id="kpi-tracker-comment-button">
        <Badge invisible={!comment} variant="dot" color="error">
          <SmsOutlinedIcon fontSize="small" />
        </Badge>
      </IconButton>

      {comment && !isEditing ? (
        <KpiFloatingCommentViewer
          anchorEl={anchorEl}
          onClose={handleClose}
          comment={comment}
          onEdit={handleEdit}
        />
      ) : (
        <KpiFloatingComment
          anchorEl={anchorEl}
          onSubmit={handleSubmit}
          onClose={handleClose}
          value={value}
          onChange={setValue}
          loading={createLoading || updateLoading}
          isEditing={isEditing}
        />
      )}
    </TableCell>
  );
};
